import { Quote } from '@/model/quotesModel';
import { extractInvestmentMaxs, extractInvestmentMins, percentile } from '@/utils/investmentBounds';
import { useMemo } from 'react';
import type { SliderBounds } from '@/features/shop-insurance';

// investmentMin/Max are stored per quote row and a small tail carries implausible values, so
// the extremes are not safe slider bounds — a single row with a $3.4M max would otherwise
// raise the ceiling for the whole grid and let an advisor model an unissuable premium.
const BOUND_PERCENTILE = 0.95;

const DEFAULT_BOUNDS: SliderBounds = {
  min: 100000,
  max: 200000,
  midPoint: 150000,
  defaultValue: 100000,
  isCalculatedFromQuotes: false,
};

function matchesSliderTab(quote: Quote, selectedTab: ShopInsuranceTab): boolean {
  if (selectedTab === 'joint') {
    return quote.policyType === 'Joint Life';
  }
  // The Couples Discount tab was removed — its quotes are folded into the Single tab.
  return (
    quote.policyType === 'Single Life' ||
    quote.policyType === 'Standard' ||
    quote.policyType === 'Couples Discount'
  );
}

export function calculateSliderBounds(
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
): SliderBounds {
  if (!quotes || quotes.length === 0) {
    return DEFAULT_BOUNDS;
  }

  const filteredQuotes = quotes.filter(quote => matchesSliderTab(quote, selectedTab));

  if (filteredQuotes.length === 0) {
    return DEFAULT_BOUNDS;
  }

  const investmentMins = extractInvestmentMins(filteredQuotes);
  const investmentMaxs = extractInvestmentMaxs(filteredQuotes);

  if (investmentMins.length === 0 || investmentMaxs.length === 0) {
    return DEFAULT_BOUNDS;
  }

  // Percentiles keep outlier bounds from setting the range; widening back out to the
  // actually-quoted premiums keeps every real, known-issuable premium reachable. Both
  // arrays are non-empty per the guard above, so percentile always returns a value.
  const boundsMin = percentile(investmentMins, 1 - BOUND_PERCENTILE, 'up')!;
  const boundsMax = percentile(investmentMaxs, BOUND_PERCENTILE, 'down')!;

  const quotedPremiums = filteredQuotes
    .map(quote => quote.premiumAmount)
    .filter(premium => typeof premium === 'number' && premium > 0);

  const finalSliderMin = quotedPremiums.reduce((low, premium) => Math.min(low, premium), boundsMin);
  const finalSliderMax = quotedPremiums.reduce(
    (high, premium) => Math.max(high, premium),
    boundsMax,
  );

  if (finalSliderMax <= finalSliderMin) {
    return DEFAULT_BOUNDS;
  }

  const midPoint = Math.floor((finalSliderMin + finalSliderMax) / 2);

  const defaultValue =
    filteredQuotes[0].premiumAmount > 0
      ? Math.max(finalSliderMin, Math.min(finalSliderMax, filteredQuotes[0].premiumAmount))
      : midPoint;

  return {
    min: finalSliderMin,
    max: finalSliderMax,
    midPoint,
    defaultValue,
    isCalculatedFromQuotes: true,
  };
}

export const useSliderBounds = (
  quotes: Quote[] | undefined,
  selectedTab: ShopInsuranceTab,
): SliderBounds => {
  return useMemo(() => {
    try {
      return calculateSliderBounds(quotes, selectedTab);
    } catch (error) {
      console.error('Error calculating slider bounds:', error);
      return DEFAULT_BOUNDS;
    }
  }, [quotes, selectedTab]);
};
